import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Heart, Star, ArrowRight, MessageCircle, Sparkles } from 'lucide-react'
import { useUserStore } from '../stores/userStore'
import toast from 'react-hot-toast'
import SubscriptionGuard from '../components/SubscriptionGuard'
import UsageGuard from '../components/UsageGuard'
import InteractiveMatchmakingForm from '../components/InteractiveMatchmakingForm'
import { useNavigate } from 'react-router-dom'

interface BirthData {
  name: string;
  day: number;
  month: number;
  year: number;
  hour: number;
  min: number;
  lat: number;
  lon: number;
  tzone: number;
  place_of_birth: string;
}

interface MatchmakingReport {
  analysis: string;
  chartData: {
    charts: {
      match_ashtakoot_points: {
        total: {
          total_points: number;
          received_points: number;
        };
      };
      match_simple_report: {
        ashtakoota: {
          status: boolean;
          received_points: number;
        };
        manglik: {
          status: boolean;
          male_percentage: number;
          female_percentage: number;
        };
        rajju_dosha: {
          status: boolean;
        };
        vedha_dosha: {
          status: boolean;
        };
        conclusion: {
          match_report: string;
        };
      };
    };
  };
  matchType: string;
  focusAreas: string[];
  confidence: number;
  sources: string[];
  male: string;
  female: string;
  importedCharts: string[];
  timestamp: string;
}

const Matchmaking = () => {
  const { user } = useUserStore()
  const navigate = useNavigate()
  const [showMatchmakingForm, setShowMatchmakingForm] = useState(false)
  const [matchmakingReport, setMatchmakingReport] = useState<MatchmakingReport | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [history, setHistory] = useState<any[]>([])
  const [maleData] = useState<BirthData>({
    name: '',
    day: 1,
    month: 1,
    year: 1990,
    hour: 12,
    min: 0,
    lat: 0,
    lon: 0,
    tzone: 5.5,
    place_of_birth: ''
  })
  const [femaleData] = useState<BirthData>({
    name: '',
    day: 1,
    month: 1,
    year: 1990,
    hour: 12,
    min: 0,
    lat: 0,
    lon: 0,
    tzone: 5.5,
    place_of_birth: ''
  })

  const handleFormComplete = async (maleData: BirthData, femaleData: BirthData) => {
    try {
      setIsAnalyzing(true)
      console.log('Starting matchmaking analysis with data:', { maleData, femaleData })
      
      const requestBody = {
        maleData: {
          name: maleData.name,
          day: maleData.day,
          month: maleData.month,
          year: maleData.year,
          hour: maleData.hour,
          minute: maleData.min,
          latitude: maleData.lat,
          longitude: maleData.lon,
          timezone: maleData.tzone,
          placeOfBirth: maleData.place_of_birth
        },
        femaleData: {
          name: femaleData.name,
          day: femaleData.day,
          month: femaleData.month,
          year: femaleData.year,
          hour: femaleData.hour,
          minute: femaleData.min,
          latitude: femaleData.lat,
          longitude: femaleData.lon,
          timezone: femaleData.tzone,
          placeOfBirth: femaleData.place_of_birth
        },
        matchType: 'marriage'
      }
      
      console.log('Request body:', requestBody)
      
      const response = await fetch('/api/matchmaking/comprehensive-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // Include userId (backend schema now accepts optional userId)
        body: JSON.stringify({ ...requestBody, userId: user?.uid })
      })

      console.log('Response status:', response.status)
      console.log('Response headers:', response.headers)

      const result = await response.json()
      console.log('Response result:', result)

      if (result.success) {
        setMatchmakingReport(result)
        setShowMatchmakingForm(false)
        toast.success('Matchmaking analysis completed successfully!')
      } else {
        console.error('Backend returned error:', result)
        toast.error(result.error || 'Failed to generate matchmaking analysis')
      }
    } catch (error) {
      console.error('Error generating matchmaking analysis:', error)
      toast.error('An error occurred while generating the analysis')
    }
    finally {
      setIsAnalyzing(false)
    }
  }

  // Load recent matchmaking history for this user
  useEffect(() => {
    const loadHistory = async () => {
      try {
        if (!user?.uid) return;
        const res = await fetch(`/api/matchmaking/history/${user.uid}`);
        const data = await res.json();
        if (data.success) setHistory(data.items || []);
      } catch (e) {
        console.error('Failed to load matchmaking history', e);
      }
    };
    loadHistory();
  }, [user?.uid]);

  const getCompatibilityScore = (report: MatchmakingReport) => {
    if (!report?.chartData?.charts?.match_ashtakoot_points?.total) {
      return 0;
    }
    const totalPoints = report.chartData.charts.match_ashtakoot_points.total.total_points;
    const receivedPoints = report.chartData.charts.match_ashtakoot_points.total.received_points;
    return Math.round((receivedPoints / totalPoints) * 100);
  }

  const getCompatibilityColor = (score: number) => {
    if (score >= 80) return 'text-green-400'
    if (score >= 60) return 'text-yellow-400'
    return 'text-red-400'
  }

  const getCompatibilityText = (score: number) => {
    if (score >= 80) return 'Excellent Match'
    if (score >= 60) return 'Good Match'
    if (score >= 40) return 'Average Match'
    return 'Poor Match'
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h1 className="mobile-text-large mb-4">
            <span className="gradient-text">Matchmaking</span>
            <br />
            <span className="text-white">Compatibility Analysis</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Discover your cosmic compatibility with detailed Ashtakoota analysis
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="mobile-spacing">
          {/* Start Analysis Button */}
          {!matchmakingReport && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mobile-card"
            >
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-12 h-12 text-purple-400" />
                </div>
                <h2 className="mobile-text-medium text-white mb-4">
                  Compatibility Analysis
                </h2>
                <p className="text-gray-300 mb-8 leading-relaxed">
                  Enter birth details for both partners to get a detailed Ashtakoota compatibility analysis
                </p>
                <button
                  onClick={() => setShowMatchmakingForm(true)}
                  className="mobile-button-primary inline-flex items-center space-x-2"
                >
                  <span>Start Compatibility Analysis</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}

          {/* History Section */}
          {history.length > 0 && (
            <div className="mobile-card">
              <h3 className="mobile-text-medium text-white mb-4 flex items-center">
                <Sparkles className="w-6 h-6 mr-3" />
                Recent Matchmaking History
              </h3>
              <div className="space-y-3">
                {history.map((h: any) => {
                  const entry = h?.data?.data || h?.data || {};
                  const maleName = entry?.male?.name || entry?.male || '';
                  const femaleName = entry?.female?.name || entry?.female || '';
                  return (
                  <button
                    key={h.id}
                    onClick={() => {
                      // Restore full report from history using stored chartData
                      const chartData = entry?.chartData;
                      const totalPoints = chartData?.charts?.match_ashtakoot_points?.total?.total_points || 36;
                      const receivedPoints = chartData?.charts?.match_ashtakoot_points?.total?.received_points || 0;
                      const restored = {
                        success: true,
                        analysis: entry?.summary || 'Previous analysis loaded from history',
                        chartData,
                        matchType: entry?.matchType || 'marriage',
                        confidence: 0.9,
                        sources: ['History'],
                        male: maleName,
                        female: femaleName,
                        importedCharts: Object.keys(chartData?.charts || {}),
                        timestamp: entry?.timestamp || h.created_at,
                        // keep userId for chat page so it can recover charts if needed
                        userId: (user as any)?.uid,
                        // convenience: computed score (UI already recomputes as needed)
                        _score: Math.round((receivedPoints / totalPoints) * 100)
                      } as unknown as MatchmakingReport;
                      setMatchmakingReport(restored);
                      toast.success('Loaded previous matchmaking result');
                    }}
                    className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-gray-200 text-sm">
                        {maleName} ❤ {femaleName}
                      </div>
                      <div className="text-gray-400 text-xs">
                        {new Date(h.created_at).toLocaleString()}
                      </div>
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Analyzing Loader */}
          {isAnalyzing && (
            <div className="mobile-card">
              <div className="flex items-center justify-center py-10 space-x-3 text-purple-200">
                <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                </svg>
                <span>Analyzing compatibility...</span>
              </div>
            </div>
          )}

          {/* Matchmaking Results */}
          {matchmakingReport && !isAnalyzing && (
            <SubscriptionGuard feature="matchmaking analysis">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mobile-spacing"
              >
                {/* Overall Compatibility Score */}
                <div className="mobile-card">
                  <div className="text-center mb-6">
                    <h2 className="mobile-text-medium text-white mb-4 flex items-center justify-center">
                      <Heart className="w-6 h-6 mr-3" />
                      Overall Compatibility Score
                    </h2>
                    
                    <div className="text-6xl font-bold mb-2">
                      <span className={getCompatibilityColor(getCompatibilityScore(matchmakingReport))}>
                        {getCompatibilityScore(matchmakingReport)}%
                      </span>
                    </div>
                    
                    <p className="text-xl text-gray-300 mb-4">
                      {getCompatibilityText(getCompatibilityScore(matchmakingReport))}
                    </p>
                    
                    <p className="text-gray-400">
                      {matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.received_points || 0} out of {matchmakingReport?.chartData?.charts?.match_ashtakoot_points?.total?.total_points || 36} points
                    </p>
                  </div>
                </div>

                {/* Ashtakoota Analysis */}
                <div className="mobile-card">
                  <h3 className="mobile-text-medium text-white mb-6 flex items-center">
                    <Star className="w-6 h-6 mr-3" />
                    Compatibility Analysis
                  </h3>
                  
                  <div className="bg-white/5 p-6 rounded-lg">
                    <div className="prose prose-invert max-w-none space-y-4">
                      {/* Render markdown-like headers and bolds */}
                      <div
                        className="text-gray-300 leading-relaxed markdown-render"
                        dangerouslySetInnerHTML={{
                          __html: (matchmakingReport.analysis || '')
                            .replace(/###\s+([^\n]+)/g, '<h3 class="text-xl font-bold text-white mt-4 mb-2">$1</h3>')
                            .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white">$1</strong>')
                            .replace(/\n\n/g, '<br/><br/>')
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="mobile-card">
                  <h3 className="mobile-text-medium text-white mb-6 flex items-center">
                    <Sparkles className="w-6 h-6 mr-3" />
                    Key Metrics
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/5 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-white mb-2">Manglik Status</h4>
                      <p className="text-gray-300 text-sm mb-2">
                        Male: {matchmakingReport?.chartData?.charts?.match_simple_report?.manglik?.male_percentage || 0}%
                      </p>
                      <p className="text-gray-300 text-sm">
                        Female: {matchmakingReport?.chartData?.charts?.match_simple_report?.manglik?.female_percentage || 0}%
                      </p>
                    </div>
                    
                    <div className="bg-white/5 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-white mb-2">Rajju Dosha</h4>
                      <p className={`text-sm ${matchmakingReport?.chartData?.charts?.match_simple_report?.rajju_dosha?.status ? 'text-red-400' : 'text-green-400'}`}>
                        {matchmakingReport?.chartData?.charts?.match_simple_report?.rajju_dosha?.status ? 'Present' : 'Not Present'}
                      </p>
                    </div>
                    
                    <div className="bg-white/5 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-white mb-2">Vedha Dosha</h4>
                      <p className={`text-sm ${matchmakingReport?.chartData?.charts?.match_simple_report?.vedha_dosha?.status ? 'text-red-400' : 'text-green-400'}`}>
                        {matchmakingReport?.chartData?.charts?.match_simple_report?.vedha_dosha?.status ? 'Present' : 'Not Present'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Match Report */}
                <div className="mobile-card">
                  <h3 className="mobile-text-medium text-white mb-6 flex items-center">
                    <MessageCircle className="w-6 h-6 mr-3" />
                    Match Report
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white/5 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-white mb-2">Ashtakoota Conclusion</h4>
                      <p className="text-gray-300">{matchmakingReport?.chartData?.charts?.match_simple_report?.conclusion?.match_report || 'Analysis not available'}</p>
                    </div>
                    
                    <div className="bg-white/5 p-4 rounded-lg">
                      <h4 className="text-lg font-semibold text-white mb-2">Analysis Details</h4>
                      <p className="text-gray-300 text-sm">
                        <strong>Match Type:</strong> {matchmakingReport?.matchType || 'Not specified'}<br/>
                        <strong>Focus Areas:</strong> {matchmakingReport?.focusAreas?.join(', ') || 'Not specified'}<br/>
                        <strong>Confidence:</strong> {matchmakingReport?.confidence || 0}%<br/>
                        <strong>Charts Analyzed:</strong> {matchmakingReport?.importedCharts?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Chat Interface */}
                <div className="mobile-card">
                  <UsageGuard feature="chat" onUsageIncrement={() => {
                    navigate('/matchmaking-chat', {
                      state: {
                        matchmakingReport,
                        maleData,
                        femaleData
                      }
                    })
                  }}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        navigate('/matchmaking-chat', {
                          state: {
                            matchmakingReport,
                            maleData,
                            femaleData
                          }
                        })
                      }}
                      className="mobile-button-primary w-full flex items-center justify-center space-x-2"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span>Ask Questions About Your Compatibility</span>
                    </motion.button>
                  </UsageGuard>
                </div>

                {/* New Analysis Button */}
                <div className="mobile-card">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setMatchmakingReport(null)}
                    className="mobile-button-secondary w-full flex items-center justify-center space-x-2"
                  >
                    <ArrowRight className="w-4 h-4" />
                    <span>New Compatibility Analysis</span>
                  </motion.button>
                </div>
              </motion.div>
            </SubscriptionGuard>
          )}


        </div>
      </div>

      {/* Interactive Matchmaking Form Modal */}
      {showMatchmakingForm && (
        <InteractiveMatchmakingForm
          onComplete={handleFormComplete}
          onCancel={() => setShowMatchmakingForm(false)}
        />
      )}
    </div>
  )
}

export default Matchmaking 